import { TasksService } from '@/services/masters/user/tasksService';
import { globalApiService } from '../globalApiSlice';
import type { StoreBaseResponse } from '@/types/types';
import { createCustomError, createCustomResponse } from '@/helpers/utils';
import type {
  AddTaskRequestInterface,
  MyTask,
  TaskAssignedByMe,
} from '@/types/user/task-types';
import type { RequestOptions } from '@/services/apiService';

const service = new TasksService();

const tasksApi = globalApiService.injectEndpoints({
  endpoints: (build) => ({
    addTask: build.mutation<StoreBaseResponse<null>, AddTaskRequestInterface>({
      queryFn: async (body) => {
        try {
          const response = await service.addTask(body);
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
      invalidatesTags: (_, error) => (error ? [] : ['tasks_newTask']),
    }),
    getMyTasks: build.query<
      StoreBaseResponse<MyTask[]>,
      RequestOptions['params']
    >({
      queryFn: async (body) => {
        try {
          const response = await service.getMyTasks(body);
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
      providesTags: ['tasks_newTask'],
    }),
    getMyTasksPending: build.mutation<
      StoreBaseResponse<MyTask[]>,
      RequestOptions['params']
    >({
      queryFn: async (body) => {
        try {
          const response = await service.getMyTasks({
            ...body,
            status: 'pending',
          });
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
    }),
    getMyTasksCompleted: build.mutation<
      StoreBaseResponse<MyTask[]>,
      RequestOptions['params']
    >({
      queryFn: async (body) => {
        try {
          const response = await service.getMyTasks({
            ...body,
            status: 'completed',
          });
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
    }),
    getTasksAssignedByMe: build.query<
      StoreBaseResponse<TaskAssignedByMe[]>,
      RequestOptions['params']
    >({
      queryFn: async (body) => {
        try {
          const response = await service.getTasksAssignedByMe(body);
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
      providesTags: ['tasks_newTask'],
    }),
    getTasksAssignedByMePending: build.mutation<
      StoreBaseResponse<TaskAssignedByMe[]>,
      RequestOptions['params']
    >({
      queryFn: async (body) => {
        try {
          const response = await service.getTasksAssignedByMe({
            ...body,
            status: 'pending',
          });
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
    }),
    getTasksAssignedByMeCompleted: build.mutation<
      StoreBaseResponse<TaskAssignedByMe[]>,
      RequestOptions['params']
    >({
      queryFn: async (body) => {
        try {
          const response = await service.getTasksAssignedByMe({
            ...body,
            status: 'completed',
          });
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
    }),
    markTask: build.mutation<
      StoreBaseResponse<null>,
      { task_id: number; status: string }
    >({
      queryFn: async (body) => {
        try {
          const response = await service.markTask(body);
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
    }),
    removeTaskByAssigner: build.mutation<StoreBaseResponse<null>, number>({
      queryFn: async (number) => {
        try {
          const response = await service.deleteTask(number);
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
    }),
  }),
});

export const {
  useGetMyTasksQuery,
  useGetTasksAssignedByMeQuery,

  useLazyGetTasksAssignedByMeQuery,
  useLazyGetMyTasksQuery,
  useAddTaskMutation,
  useMarkTaskMutation,
  useRemoveTaskByAssignerMutation,
  useGetMyTasksPendingMutation,
  useGetMyTasksCompletedMutation,
  useGetTasksAssignedByMePendingMutation,
  useGetTasksAssignedByMeCompletedMutation,
} = tasksApi;
